import { useState, useEffect, useCallback, useRef } from "react";
import api from "../services/api";

export const useMessageSendingProgress = (jobId) => {
  const [progress, setProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchProgress = useCallback(async () => {
    if (!jobId) {
      console.log("⚠️ No jobId provided, skipping fetch");
      return;
    }

    try {
      setIsLoading(true);
      console.log(`📊 Fetching progress for job: ${jobId}`);
      const response = await api.get(`/progress/job/${jobId}`);

      console.log("📊 Progress response:", response.data);

      if (response.data.success) {
        setProgress(response.data.progress);
        console.log("✅ Progress updated:", {
          sent: response.data.progress.sentMessages,
          total: response.data.progress.totalMessages,
          status: response.data.progress.status,
          percentage: response.data.progress.progressPercentage,
          timeRemaining: response.data.progress.estimatedTimeRemaining,
        });

        // Stop polling if completed or failed
        if (["completed", "failed"].includes(response.data.progress.status)) {
          console.log("🏁 Job completed/failed, stopping polling");
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          // Clear from localStorage after a delay (so user can see completion)
          setTimeout(() => {
            localStorage.removeItem("activeJobId");
            console.log("🧹 Cleared activeJobId from localStorage");
          }, 10000); // Clear after 10 seconds
        }
      } else {
        console.log("⚠️ API returned success=false");
      }
    } catch (err) {
      console.error("❌ Error fetching progress:", err);
      setError(err.response?.data?.error || "Failed to fetch progress");

      // Stop polling on error
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // If 404 (job not found), clear from localStorage
      if (err.response?.status === 404) {
        localStorage.removeItem("activeJobId");
        console.log("🧹 Job not found, cleared activeJobId from localStorage");
      }
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  // Start polling when jobId is provided
  useEffect(() => {
    if (jobId) {
      console.log("🚀 Starting progress tracking for job:", jobId);

      // Fetch immediately
      fetchProgress();

      // Then poll every 2 seconds
      intervalRef.current = setInterval(() => {
        console.log("📊 Polling progress for job:", jobId);
        fetchProgress();
      }, 2000);
    } else {
      // jobId was cleared (user closed the widget) — reset progress so the
      // widget unmounts immediately instead of waiting for the next render.
      setProgress(null);
    }

    return () => {
      if (intervalRef.current) {
        console.log("🛑 Stopping progress tracking for job:", jobId);
        clearInterval(intervalRef.current);
      }
    };
  }, [jobId, fetchProgress]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  return {
    progress,
    isLoading,
    error,
    stopPolling,
    refetch: fetchProgress,
  };
};
